import { message, Select } from "antd";
import { useBulkSaveSessionResponses } from "../../api/SessionResponsesAPI";
import { useNavigate } from "react-router-dom";
import { showNotification } from "../common/Notification";
import Popup from "../common/Popup";
import SessionCompletedModal from "../tool/SessionCompletedModal";
import { useState } from "react";
import { useProfile } from "../../api/profileApi";
import { PLANS, normalizePlan } from "../../constants/plans";
import { useSession } from "../../context/SessionContext";
import { PhaseDefs } from "../../constants/definitions";
import { useUpdateSessionStatus } from "../../api/SessionsAPI";

const ControlPanel = ({
  abilities,
  phaseName,
  sessionInfo,
  currentAbility,
  setCurrentAbility,
  toggledCompetencies,
  setToggledCompetencies,
  selectedCompetencies,
  competenciesByAbility,
  setHasUnsavedChanges,
}) => {
  const navigate = useNavigate();
  const [clearOpen, setClearOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { sessionId } = useSession();
  const [isSaving, setIsSaving] = useState(false);
  const { data: profile } = useProfile();
  
  const effectiveSessionId = sessionInfo?.id || sessionId;
  
  // Check if this is a cohort session
  const isCohortSession = sessionInfo?.cohort_id;
  
  const { mutateAsync: bulkSaveSessionResponses } = useBulkSaveSessionResponses();
  const { mutateAsync: updateSessionStatus } = useUpdateSessionStatus();

  const phases = ["empathize", "define", "ideate", "prototype", "test", "communicate"];

  const saveSession = async () => {
    try {
      setIsSaving(true);
      console.log("Saving session...");
      console.log("Session ID:", effectiveSessionId);
      console.log("Toggled competencies:", toggledCompetencies);
      
      // Save all toggled competencies at once
      await bulkSaveSessionResponses({
        sessionId: effectiveSessionId,
        competencyIds: toggledCompetencies
      });
      
      // Mark as having no unsaved changes
      setHasUnsavedChanges(false);

      console.log("Session saved successfully!");
      showNotification("success", "Success", "Session saved successfully!");

      // Always navigate to dashboard after successful save
      navigate("/");
    } catch (error) {
      console.error("Error saving session:", error);
      showNotification("error", "Error Saving Session", error.message || "Failed to save session");
    } finally {
      setIsSaving(false);
    }
  };

  const completeSession = async () => {
    try {
      setIsSaving(true);
      console.log("Completing session...");
      
      // First save all toggled competencies
      await bulkSaveSessionResponses({
        sessionId: effectiveSessionId,
        competencyIds: toggledCompetencies
      });
      
      // Then update the session status to completed
      await updateSessionStatus({
        sessionId: effectiveSessionId,
        status: "completed"
      });
      
      // Mark as having no unsaved changes
      setHasUnsavedChanges(false);

      console.log("Session completed successfully!");
      showNotification("success", "Success", "Session completed successfully!");
      
      // Close the completion confirmation modal
      setCompleteOpen(false);
      
      // Show the completed modal
      setCompletedModalOpen(true);
    } catch (error) {
      console.error("Error completing session:", error);
      showNotification("error", "Error Completing Session", error.message || "Failed to complete session");
      setCompleteOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhaseChange = (newPhase) => {
    if (newPhase === phaseName) return;
    // Directly navigate to the new phase while maintaining state
    navigate(`/session/${effectiveSessionId}/${newPhase}`);
  };

  return (
    <div className="fixed w-[320px] pt-4 pb-20 bg-white h-full z-40 right-0 text-left text-primary overflow-y-auto">
      <div className="px-4">
        <h1 className="font-extrabold text-base">
          Phase: {phaseName.toUpperCase()}
        </h1>
        <div className="mt-2">
          <Select
            className="w-full"
            value={phaseName}
            onChange={handlePhaseChange}
          >
            {phases.map((phase) => (
              <Select.Option key={phase} value={phase}>
                <p className="text-xs break-words whitespace-normal leading-tight">
                {phase.toUpperCase()}
                </p>
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="relative mt-3">
          <p className={`text-xs ${!isExpanded && 'line-clamp-5'}`}>
            {PhaseDefs[phaseName.toUpperCase()]}
          </p>
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-primary hover:text-primaryLight underline mt-1"
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        </div>
      </div>
      <p className="w-full bg-primary text-white mt-4 mb-2 py-1 px-6 text-xs">
        Select Ability
      </p>
      <div className="px-4">
        <p className="text-xs italic">
          Please choose only the abilities that are relevant to your design
          project
        </p>
        <Select
          className="w-full mt-4"
          value={currentAbility ? currentAbility.name : undefined}
          onChange={(value) => {
            const selectedAbility = abilities.find((a) => a.name === value);
            setCurrentAbility(selectedAbility);
          }}
        >
          {abilities.map((ability, i) => {
            return (
              <Select.Option key={i} value={ability.name}>
                <p className="text-xs break-words whitespace-normal leading-tight">
                  {ability.name}
                </p>
              </Select.Option>
            );
          })}
        </Select>
      </div>
      <p className="w-full bg-primary text-white mt-4 mb-2 py-1 px-6 text-xs">
        Session Details
      </p>
      <div className="px-4 mt-4 text-xs">
        <p>
          <span className="font-semibold">Session ID</span>: {effectiveSessionId || "No active session"}
        </p>
        {isCohortSession && (
          <p className="mt-1">
            <span className="font-semibold">Cohort Session ID</span>: {sessionInfo.cohort_id}
          </p>
        )}
        <p className="mt-1">
          <span className="font-semibold">Selected Competencies</span>: {toggledCompetencies.length}
        </p>

        <div className="mt-10">
          <button
            onClick={saveSession}
            disabled={isSaving}
            className={`w-full ${isSaving ? 'bg-gray-400' : 'bg-primary hover:bg-primaryLight'} text-white mb-3 cursor-pointer p-2 text-xs`}
          >
            {isSaving ? "Saving..." : "Save Responses and Exit"}
          </button>
         
          <button
            onClick={() => setClearOpen(true)}
            disabled={isSaving}
            className={`w-full border-2 ${isSaving ? 'border-gray-400 text-gray-400' : 'border-primary hover:bg-primaryLight hover:border-primaryLight hover:text-white'} p-2 text-xs cursor-pointer mb-3`}
          >
            Exit Without Saving
          </button>

          <button
            onClick={() => setCompleteOpen(true)}
            disabled={isSaving}
            className={`w-full px-4 py-2 font-medium bg-accent rounded hover:bg-accentLight disabled:opacity-50 disabled:cursor-not-allowed text-black`}
          >
            {isSaving ? "Completing..." : "Complete Session"}
          </button>
          
          <SessionCompletedModal 
            open={completedModalOpen}
            onClose={() => setCompletedModalOpen(false)}
          />
          
          <Popup
            title="Exit without saving?"
            open={clearOpen}
            content={
              <p>
                Are you sure you want to exit without saving your session? Any
                unsaved selections will be lost.
              </p>
            }
            onOk={() => {
              setClearOpen(false);
              navigate("/");
            }}
            onCancel={() => setClearOpen(false)}
          />
          
          <Popup
            title="Complete Session?"
            open={completeOpen}
            content={
              <div>
                <p className="text-red-500 font-semibold mb-2">Warning: This action cannot be undone.</p>
                <p>
                  Once a session is completed, you will not be able to make any further changes 
                  to your competency selections. All current selections will be saved.
                </p>
                <p className="mt-2">
                  Are you sure you want to complete this session?
                </p>
              </div>
            }
            onOk={completeSession}
            onCancel={() => setCompleteOpen(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default ControlPanel;
